import { createAudioAnalyser } from './audio';
import { createAuthState } from './auth';
import { initDiscord } from './discord';
import { drawKaraoke, type LyricLine } from './karaoke';
import { fetchLyrics } from './lyrics';
import { NowPlayingPoller, type NowPlayingState } from './nowplaying';
import { parseTitle } from './title-parser';
import { binsToBarHeights, draw, interpProgress, resetVisualizer } from './visualizer';
import { fetchQueue, deleteQueueItem, reorderQueue, getUser, getMe, djPlay, djPause, djSkip, fetchListeners, fetchSyncInfo, type Listener } from './api';
import { renderQueue } from './queue_view';

declare const __APP_ID__: string;
const BOT_USER_ID = Number(__APP_ID__);

function trackKey(s: NowPlayingState): string {
  if (s.spotifyMetadata) return `sp:${s.spotifyMetadata.trackId}`;
  // Queue mode: each /play assigns a new queue_item.id, but the poller
  // doesn't expose it — title is unique enough in practice.
  return `q:${s.title}`;
}

function lyricsQuery(s: NowPlayingState): { artist: string; track: string } | null {
  if (s.spotifyMetadata) {
    const firstArtist = (s.spotifyMetadata.artists.split(',')[0] || '').trim();
    return firstArtist ? { artist: firstArtist, track: s.spotifyMetadata.name } : null;
  }
  if (!s.title || s.title === 'Craft Music') return null;
  return parseTitle(s.title);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;');
}

function nowPlayingRequesterId(_s: NowPlayingState): number | null {
  // NowPlayingState doesn't include requester id today; we read it from the
  // currentQueueItem captured by the queue poll. See currentRequesterId
  // updated inside refreshQueue.
  return currentRequesterId;
}

let currentRequesterId: number | null = null;

interface DisplayInfo { title: string; artist: string; meta: string }
function displayInfo(s: NowPlayingState): DisplayInfo {
  if (s.spotifyMetadata) {
    return {
      title: s.spotifyMetadata.name,
      artist: s.spotifyMetadata.artists,
      meta: s.spotifyMetadata.album || 'Spotify',
    };
  }
  if (!s.title || s.title === 'Craft Music') {
    return { title: 'Craft Music', artist: '', meta: '' };
  }
  const parsed = parseTitle(s.title);
  if (parsed !== null) {
    return { title: parsed.track, artist: parsed.artist, meta: s.meta };
  }
  return { title: s.title, artist: '', meta: s.meta };
}

function showToast(msg: string): void {
  const t = document.getElementById('toast');
  if (!t) return;
  t.textContent = msg;
  t.classList.remove('hidden');
  setTimeout(() => t.classList.add('hidden'), 4000);
}

async function bootstrap(): Promise<void> {
  const canvas = document.getElementById('viz') as HTMLCanvasElement | null;
  const audio = document.getElementById('stream') as HTMLAudioElement | null;
  const titleEl = document.getElementById('title') as HTMLDivElement | null;
  const artistEl = document.getElementById('artist') as HTMLDivElement | null;
  const metaEl = document.getElementById('meta') as HTMLSpanElement | null;
  const titleMarquee = document.getElementById('title-marquee') as HTMLDivElement | null;
  const requesterChip = document.getElementById('requester-chip') as HTMLDivElement | null;
  const requesterNameEl = document.getElementById('requester-name') as HTMLSpanElement | null;
  const pausedBadge = document.getElementById('paused-badge') as HTMLDivElement | null;
  const queueList = document.getElementById('queue-list') as HTMLUListElement | null;
  const queueEmpty = document.getElementById('queue-empty') as HTMLElement | null;
  const queueCountEl = document.getElementById('queue-count') as HTMLSpanElement | null;
  if (!canvas || !audio || !titleEl || !artistEl || !metaEl || !titleMarquee
      || !requesterChip || !requesterNameEl || !queueList || !queueEmpty || !queueCountEl) {
    console.error('craft-music: missing DOM nodes');
    return;
  }

  // Tabs
  const tabs = document.querySelectorAll<HTMLButtonElement>('.tab');
  const bodies = document.querySelectorAll<HTMLDivElement>('.tab-body');
  tabs.forEach((t) => {
    t.addEventListener('click', () => {
      tabs.forEach((x) => x.classList.toggle('active', x === t));
      const tabId = t.dataset.tab;
      bodies.forEach((b) => b.classList.toggle('active', b.id === `tab-${tabId}`));
      if (tabId === 'queue') void refreshQueue();
    });
  });

  // Canvas resize
  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    canvas!.width = canvas!.clientWidth * dpr;
    canvas!.height = canvas!.clientHeight * dpr;
  }
  resize();
  window.addEventListener('resize', resize);

  // Discord SDK + OAuth (returns accessToken)
  const auth = createAuthState();
  const ctx = await initDiscord().catch((e) => {
    console.warn('craft-music: Discord SDK init failed', e);
    return null;
  });
  if (ctx?.accessToken) {
    auth.setToken(ctx.accessToken);
  }
  const activityChannelId: string | null = ctx?.channelId ?? null;

  // Now-playing
  const np = new NowPlayingPoller({ baseUrl: '', intervalMs: 2000 });
  np.start();

  // Audio + analyser
  const aa = createAudioAnalyser(audio, { fftSize: 512, smoothing: 0.8 });
  async function unlockAudio(): Promise<void> {
    try { await aa.resume(); } catch { /* ignore */ }
    try { await audio!.play(); } catch (e) { console.warn('craft-music: audio.play() rejected', e); }
  }
  await unlockAudio();
  if (aa.ctx.state === 'suspended' || audio.paused) {
    document.getElementById('root')?.addEventListener(
      'click', () => void unlockAudio(), { once: true });
  }

  // Karaoke sync offset — bot reports its measured voice-pipeline latency
  // via /api/sync-info (ffmpeg + discord.py + voice gateway RTT + estimated
  // user jitter). We poll it every 30s and use it as the karaoke delay.
  // Fallback to 800ms if the endpoint is unreachable.
  // Karaoke offset = (mode baseline) + (user nudge). Mode baseline depends
  // on listening source: slider > 0 → 0 (iframe), slider = 0 → bot's measured
  // Discord-pipeline lag. User can fine-tune with the +/- nudge buttons.
  let cachedDiscordOffsetS = 4.0;
  let userVolume = 0;
  const userNudgeS = 0;  // legacy hook for currentOffset(); UI removed
  function currentOffset(): number {
    const base = userVolume > 0 ? 0 : cachedDiscordOffsetS;
    return base + userNudgeS;
  }
  let karaokeOffsetS = currentOffset();
  async function refreshSyncOffset(): Promise<void> {
    try {
      const info = await fetchSyncInfo();
      cachedDiscordOffsetS = info.total_offset_ms / 1000;
      karaokeOffsetS = currentOffset();
    } catch { /* keep previous */ }
  }
  // Pollers are tracked as {fn, ms, handle} so visibilitychange can
  // pause them while the iframe is backgrounded and resume on return.
  type Poller = { fn: () => void; ms: number; handle: ReturnType<typeof setInterval> | null };
  const pollers: Poller[] = [];
  function addPoller(fn: () => void, ms: number): void {
    const p: Poller = { fn, ms, handle: setInterval(fn, ms) };
    pollers.push(p);
  }
  void refreshSyncOffset();
  addPoller(() => void refreshSyncOffset(), 30000);

  // Dismissable tip — once dismissed, never shown again for this browser.
  const vibesTip = document.getElementById('vibes-tip') as HTMLDivElement | null;
  const vibesTipDismiss = document.getElementById('vibes-tip-dismiss') as HTMLButtonElement | null;
  if (vibesTip && localStorage.getItem('craftmusic.tipDismissed') === '1') {
    vibesTip.classList.add('hidden');
  }
  vibesTipDismiss?.addEventListener('click', () => {
    vibesTip?.classList.add('hidden');
    localStorage.setItem('craftmusic.tipDismissed', '1');
  });

  // Volume slider — persists per-user via localStorage
  const volSlider = document.getElementById('vol-slider') as HTMLInputElement | null;
  const volIcon = document.getElementById('vol-icon') as HTMLSpanElement | null;
  const savedVol = Number(localStorage.getItem('craftmusic.volume') ?? '30');
  function applyVolume(v: number): void {
    aa.setVolume(v / 100);
    userVolume = v / 100;
    karaokeOffsetS = currentOffset();
    if (volIcon) volIcon.textContent = v === 0 ? '🔇' : v < 50 ? '🔈' : '🔊';
  }
  if (volSlider) {
    volSlider.value = String(savedVol);
    applyVolume(savedVol);
    volSlider.addEventListener('input', () => {
      const v = Number(volSlider.value);
      applyVolume(v);
      localStorage.setItem('craftmusic.volume', String(v));
      void unlockAudio();
    });
  }

  // User name cache: Discord ID → {name, expiresAt}. Negative results expire
  // after 30s so transient bot/cache misses retry instead of sticking on 'Unknown'.
  interface CacheEntry { name: string; isFallback: boolean; expiresAt: number; }
  const userCache = new Map<number, CacheEntry>();
  const userFetching = new Set<number>();
  userCache.set(BOT_USER_ID, { name: 'Radio', isFallback: false, expiresAt: Infinity });
  function resolveUser(userId: number): string {
    const cached = userCache.get(userId);
    if (cached && (!cached.isFallback || cached.expiresAt > performance.now())) {
      return cached.name;
    }
    if (!userFetching.has(userId)) {
      userFetching.add(userId);
      void getUser(userId).then((u) => {
        userCache.set(userId, { name: u.display_name, isFallback: false, expiresAt: Infinity });
        userFetching.delete(userId);
        void refreshQueue();
      }).catch(() => {
        userCache.set(userId, {
          name: 'Unknown',
          isFallback: true,
          expiresAt: performance.now() + 5000,
        });
        userFetching.delete(userId);
        void refreshQueue();
      });
    }
    return cached?.name ?? '…';
  }

  // DJ status + local user identity — fired in PARALLEL and non-blocking.
  // Bootstrap continues immediately; these resolve in the background.
  let isDj = false;
  let localUser: { id: string; display_name: string; avatar_url: string } | null = null;
  void auth.checkIsDj().then((v) => {
    isDj = v;
    if (isDj && tPlayPause) tPlayPause.classList.remove('hidden');
    if (isDj && tSkip) tSkip.classList.remove('hidden');
    void refreshQueue();
  }).catch(() => undefined);
  const tok = auth.getToken();
  if (tok) {
    void getMe(tok).then((me) => {
      localUser = { id: me.id, display_name: me.display_name, avatar_url: me.avatar_url };
      void refreshListeners();
    }).catch(() => undefined);
  }

  // Karaoke state (preserved from v1)
  let currentTrackKey = '';
  let trackStartedAtAudioTime = 0;
  let currentLyrics: LyricLine[] | null = null;
  let currentIntroPaddingS = 0;  // seconds of YouTube intro before song begins
  // When a track-change fires, engine takes seconds to actually start
  // streaming the new track's audio (yt-dlp resolve + ffmpeg startup).
  // During that gap /stream.opus is silent. We re-anchor trackStart on
  // a silence → sound transition after the change event. Requiring real
  // silence first avoids re-anchoring on the previous track's tail.
  let awaitingFirstSound = false;
  let sawSilenceSinceChange = false;
  let trackChangeAtPerf = 0;
  const SILENCE_BIN_SUM = 200;
  const SOUND_BIN_SUM = 2000;
  const ONSET_TIMEOUT_MS = 12000;

  function onMaybeTrackChange(state: NowPlayingState): void {
    const key = trackKey(state);
    if (key === currentTrackKey) return;
    currentTrackKey = key;
    // Mid-song joins: engine's position_seconds tells us how far into the
    // track we already are. Compute the audio.currentTime at which the
    // track WOULD have started, so karaokePos lines up immediately.
    const ageSec = state.lastFetchAt > 0
      ? (performance.now() - state.lastFetchAt) / 1000
      : 0;
    const enginePos = interpProgress(state.positionSeconds, ageSec);
    trackStartedAtAudioTime = audio!.currentTime - enginePos;
    currentLyrics = null;     // until fetched
    currentIntroPaddingS = 0;
    awaitingFirstSound = true;
    sawSilenceSinceChange = false;
    trackChangeAtPerf = performance.now();
    // Clear stale note particles + smoothed bar state so the new track
    // animates from a clean baseline.
    resetVisualizer();
    // parseTitle may fail for titles without "Artist - Track" format
    // (Bollywood, K-pop, single-string OST titles, etc.). In that case
    // pass the raw title as full_title and let the engine's LRCLIB
    // search fallback find it.
    if (!state.title || state.title === 'Craft Music') return;
    const query = lyricsQuery(state);
    const artist = query?.artist ?? '';
    const track = query?.track ?? '';
    void fetchLyrics(artist, track, state.title).then((result) => {
      // Race: another track may have started while we were fetching
      if (trackKey(np.getState()) !== key) return;
      if (result === null) {
        currentLyrics = null;
        return;
      }
      currentLyrics = result.lines;
      // YouTube music videos often have non-musical intros/skits. If the
      // LRCLIB song duration is shorter than the video's duration by more
      // than 10s, assume the extra is intro padding at the start.
      const latestState = np.getState();
      const videoDur = latestState.durationSeconds;
      const songDur = result.songDurationSeconds;
      if (videoDur > 0 && songDur > 0 && videoDur - songDur > 10) {
        currentIntroPaddingS = videoDur - songDur;
      }
      console.warn(`craft-music: karaoke for "${artist || state.title}" (${result.lines.length} lines, intro padding ${currentIntroPaddingS.toFixed(1)}s)`);
    });
  }

  // Queue refresh + render. /api/queue returns upcoming items (NOT the
  // currently-playing one); but its first member's "previous-of-head" is
  // what's playing. The engine puts the currently-playing item in
  // /now-playing.queue_item. We extract requested_by_id from that here
  // since NowPlayingState/Poller drops it.
  async function refreshQueue(): Promise<void> {
    try {
      // Refresh current track requester via /now-playing direct fetch so we
      // get queue_item.track.requested_by_id (not exposed by poller).
      try {
        const r = await fetch('/api/now-playing');
        if (r.ok) {
          const npj = await r.json() as { queue_item: { track: { requested_by_id: number } } | null };
          currentRequesterId = npj.queue_item ? npj.queue_item.track.requested_by_id : null;
        }
      } catch { /* keep previous value */ }
      const items = await fetchQueue();
      queueCountEl!.textContent = `(${items.length})`;
      renderQueue(queueList!, queueEmpty!, items, {
        isDj,
        botUserId: BOT_USER_ID,
        resolveUser,
        onDelete: (id) => void onDelete(id),
        onReorder: (ids) => void onReorder(ids),
      });
    } catch (e) {
      console.warn('craft-music: fetchQueue failed', e);
    }
  }

  async function onDelete(id: string): Promise<void> {
    const tok = auth.getToken();
    if (tok === null) { showToast('Not authenticated'); return; }
    try {
      await deleteQueueItem(id, tok);
      await refreshQueue();
    } catch {
      showToast("Couldn't remove that — try again");
      await refreshQueue();
    }
  }

  async function onReorder(ids: string[]): Promise<void> {
    const tok = auth.getToken();
    if (tok === null) { showToast('Not authenticated'); return; }
    try {
      await reorderQueue(ids, tok);
    } catch {
      showToast("Queue changed — your reorder didn't apply, try again");
      await refreshQueue();
    }
  }

  // Subscribe to engine WS for live queue updates
  const wsUrl = location.origin.replace(/^http/, 'ws') + '/api/events';
  function openWs(): void {
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      setTimeout(openWs, 5000);
      return;
    }
    ws.addEventListener('message', (ev) => {
      try {
        const e = JSON.parse(ev.data as string);
        if (e?.type === 'queue_changed' || e?.type === 'track_started' || e?.type === 'track_ended') {
          void refreshQueue();
        }
      } catch { /* ignore */ }
    });
    ws.addEventListener('close', () => setTimeout(openWs, 3000));
    ws.addEventListener('error', () => ws.close());
  }
  openWs();

  // Initial queue render + periodic refresh so 'Unknown' user-cache
  // entries (5s negative TTL) get a chance to retry without needing a
  // WS event or tab-click to trigger a re-render. Tracked in pollers[]
  // so visibilitychange can pause it when the iframe is backgrounded.
  void refreshQueue();
  addPoller(() => void refreshQueue(), 10000);

  // Currently-listening chip — poll every 5s
  const listenersCountEl = document.getElementById('listeners-count') as HTMLSpanElement | null;
  const listenersAvatarsEl = document.getElementById('listeners-avatars') as HTMLDivElement | null;
  async function refreshListeners(): Promise<void> {
    if (!listenersCountEl || !listenersAvatarsEl) return;
    try {
      const data = await fetchListeners(activityChannelId);
      let list = [...data.listeners] as Listener[];
      // Always include the local user — Discord member cache lag can omit
      // freshly-joined users; the user should always see themselves.
      if (localUser && !list.some((l) => l.id === localUser!.id)) {
        list = [
          { id: localUser.id, username: localUser.display_name, display_name: localUser.display_name, avatar_url: localUser.avatar_url },
          ...list,
        ];
      }
      const n = list.length;
      listenersCountEl.textContent = n === 1 ? '1 listening' : `${n} listening`;
      listenersAvatarsEl.innerHTML = '';
      const maxShow = 5;
      for (const l of list.slice(0, maxShow)) {
        const img = document.createElement('img');
        img.src = l.avatar_url;
        img.alt = l.display_name;
        img.title = l.display_name;
        img.loading = 'lazy';
        listenersAvatarsEl.appendChild(img);
      }
      if (n > maxShow) {
        const more = document.createElement('span');
        more.textContent = `+${n - maxShow}`;
        more.style.cssText = 'font-size:10px;color:#888;margin-left:4px;align-self:center';
        listenersAvatarsEl.appendChild(more);
      }
    } catch {
      listenersCountEl.textContent = '— listening';
    }
  }
  void refreshListeners();
  addPoller(() => void refreshListeners(), 5000);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      for (const p of pollers) {
        if (p.handle !== null) {
          clearInterval(p.handle);
          p.handle = null;
        }
      }
      np.stop();
    } else {
      for (const p of pollers) {
        if (p.handle === null) {
          p.fn();
          p.handle = setInterval(p.fn, p.ms);
        }
      }
      np.start();
    }
  });

  // Transport controls — play/pause for DJs, skip for DJ-OR-requester
  const tPos = document.getElementById('t-pos') as HTMLSpanElement | null;
  const tDur = document.getElementById('t-dur') as HTMLSpanElement | null;
  const tBarFill = document.getElementById('t-bar-fill') as HTMLDivElement | null;
  const tPlayPause = document.getElementById('t-playpause') as HTMLButtonElement | null;
  const tSkip = document.getElementById('t-skip') as HTMLButtonElement | null;
  // (Visibility toggled by the async checkIsDj resolver above)
  tPlayPause?.addEventListener('click', () => {
    const tok = auth.getToken();
    if (!tok) { showToast('Not authenticated'); return; }
    const state = np.getState();
    const fn = state.paused ? djPlay : djPause;
    fn(tok).catch(() => showToast("Couldn't toggle playback"));
  });
  tSkip?.addEventListener('click', () => {
    const tok = auth.getToken();
    if (!tok) { showToast('Not authenticated'); return; }
    djSkip(tok).catch(() => showToast("Couldn't skip — DJ or be the requester"));
  });

  function fmtTime(sec: number): string {
    if (!isFinite(sec) || sec < 0) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // Title marquee — show static text when it fits, scroll when it overflows.
  // The CSS animation loops from translateX(0) to translateX(-50%), which only
  // hides the seam if the track contains TWO identical copies of the title.
  let lastTitleRendered = '';
  function setTitle(next: string): void {
    if (next === lastTitleRendered) return;
    lastTitleRendered = next;
    const track = titleMarquee!.querySelector('.marquee-track') as HTMLDivElement;
    track.innerHTML = `<div id="title">${escapeHtml(next)}</div>`;
    // Measure on the next frame so layout has resolved (font load matters).
    requestAnimationFrame(() => {
      const titleNode = track.querySelector('#title') as HTMLDivElement | null;
      if (!titleNode) return;
      const cardInner = (titleMarquee!.clientWidth);
      const textWidth = titleNode.scrollWidth;
      if (textWidth > cardInner - 24) {
        // Duplicate the title so the keyframe loop is seamless.
        track.innerHTML =
          `<div id="title">${escapeHtml(next)}</div>` +
          `<div aria-hidden="true">${escapeHtml(next)}</div>`;
        // Duration scales with extra length so longer titles scroll at
        // roughly the same pixels-per-second.
        const dur = Math.max(14, Math.min(40, (textWidth + 48) / 38));
        titleMarquee!.style.setProperty('--marquee-duration', `${dur}s`);
        titleMarquee!.classList.add('scrolling');
      } else {
        titleMarquee!.classList.remove('scrolling');
      }
    });
  }

  // Render loop — Vibes tab body (karaoke OR visualizer)
  const bins = new Uint8Array(aa.analyser.frequencyBinCount);
  function frame(): void {
    const state = np.getState();
    onMaybeTrackChange(state);

    const disp = displayInfo(state);
    setTitle(disp.title);
    artistEl!.textContent = disp.artist;
    metaEl!.textContent = disp.meta || 'CRAFTCORPS';

    const reqUserId = nowPlayingRequesterId(state);
    if (reqUserId !== null) {
      const isRadio = reqUserId === BOT_USER_ID;
      const name = resolveUser(reqUserId);
      // Hide the chip while we're still resolving "Unknown" — better silence
      // than a transient placeholder.
      if (name === 'Unknown' || name === '…') {
        requesterChip!.classList.add('hidden');
      } else {
        requesterChip!.classList.remove('hidden');
        requesterChip!.classList.toggle('radio', isRadio);
        requesterNameEl!.textContent = name;
      }
    } else {
      requesterChip!.classList.add('hidden');
      requesterChip!.classList.remove('radio');
    }

    aa.readBins(bins);

    // Audio-onset detection: re-anchor trackStart on a silence→sound
    // transition after a trackChange. Requires actual silence first so we
    // don't latch onto the previous track's tail. After ONSET_TIMEOUT_MS
    // we re-anchor regardless — covers very quiet intros that never cross
    // SOUND_BIN_SUM, and the audio element being paused (gesture lock).
    if (awaitingFirstSound) {
      const elapsed = performance.now() - trackChangeAtPerf;
      let binsSum = 0;
      if (!audio!.paused) {
        for (let i = 0; i < bins.length; i++) binsSum += bins[i];
        if (!sawSilenceSinceChange && binsSum < SILENCE_BIN_SUM) {
          sawSilenceSinceChange = true;
        }
      }
      const onsetDetected = sawSilenceSinceChange && binsSum > SOUND_BIN_SUM
        && !audio!.paused;
      const timedOut = elapsed > ONSET_TIMEOUT_MS;
      if (onsetDetected || timedOut) {
        trackStartedAtAudioTime = audio!.currentTime;
        awaitingFirstSound = false;
        console.warn(
          `craft-music: onset ${onsetDetected ? 'detected' : 'TIMEOUT'} ` +
          `(sawSilence=${sawSilenceSinceChange}, elapsed=${elapsed.toFixed(0)}ms, ` +
          `binsSum=${binsSum}, paused=${audio!.paused}), ` +
          `re-anchored at audio.currentTime=${audio!.currentTime.toFixed(2)}`,
        );
      }
    }

    const rawPos = audio!.currentTime - trackStartedAtAudioTime - karaokeOffsetS;
    const lyricPos = Math.max(0, rawPos - currentIntroPaddingS);
    const trackPos = Math.max(0, rawPos);
    if (tPos) tPos.textContent = fmtTime(trackPos);
    if (tDur) tDur.textContent = fmtTime(state.durationSeconds);
    if (tBarFill && state.durationSeconds > 0) {
      const pct = Math.min(1, trackPos / state.durationSeconds);
      tBarFill.style.width = `${(pct * 100).toFixed(2)}%`;
    } else if (tBarFill) {
      tBarFill.style.width = '0%';
    }
    // Skip button: also show if the local user is the current requester
    if (tSkip && !isDj) {
      const showSkip = localUser !== null && currentRequesterId !== null &&
        String(currentRequesterId) === localUser.id;
      tSkip.classList.toggle('hidden', !showSkip);
    }

    if (currentLyrics !== null && currentLyrics.length > 0) {
      drawKaraoke(canvas!, { lines: currentLyrics, positionSeconds: lyricPos });
    } else {
      // 24 bars now; resampling inside visualizer handles smaller inputs too.
      const bars = binsToBarHeights(bins, 24);
      draw(canvas!, { bars, paused: state.paused });
    }

    // Paused indicator — visible when the engine is paused and we have a
    // track loaded (no point flashing PAUSED on the idle "Craft Music"
    // screen between tracks). Also flip the transport glyph so DJs can
    // see at a glance which action the button will take.
    const showPaused = state.paused && state.title !== 'Craft Music';
    pausedBadge?.classList.toggle('hidden', !showPaused);
    if (tPlayPause) tPlayPause.textContent = state.paused ? '▶' : '⏸';

    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

void bootstrap();
